// client/src/writer/hooks/useChapterLazyLoading.ts
// Lazy loading hook for chapter content

import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../../lib/queryClient';
import { Chapter as DbChapter, ChapterVersion as DbChapterVersion } from '@shared/schema';

interface LazyLoadedChapter extends DbChapter {
  currentVersion?: DbChapterVersion;
  versions?: DbChapterVersion[];
}

export interface ChapterLazyLoadingState {
  loadedChapters: Map<string, LazyLoadedChapter>;
  loadingChapters: Set<string>;
  loadChapter: (chapterId: string) => Promise<LazyLoadedChapter | null>;
  loadChapterVersion: (chapterId: string, versionNumber: number) => Promise<DbChapterVersion | null>;
  syncChaptersFromProject: (projectId: number) => Promise<void>;
  clearCache: () => void;
}

export const useChapterLazyLoading = (): ChapterLazyLoadingState => {
  const [loadedChapters, setLoadedChapters] = useState<Map<string, LazyLoadedChapter>>(new Map());
  const [loadingChapters, setLoadingChapters] = useState<Set<string>>(new Set());

  const loadChapter = useCallback(async (chapterId: string): Promise<LazyLoadedChapter | null> => {
    // Return cached version if available
    if (loadedChapters.has(chapterId)) {
      return loadedChapters.get(chapterId)!;
    }

    // Prevent duplicate requests
    if (loadingChapters.has(chapterId)) {
      return null;
    }

    try {
      setLoadingChapters(prev => new Set(prev).add(chapterId));

      const response = await apiRequest(`/api/chapters/${chapterId}`, 'GET');
      const chapter = await response.json() as LazyLoadedChapter;

      setLoadedChapters(prev => new Map(prev).set(chapterId, chapter));
      return chapter;
    } catch (error) {
      console.error(`Failed to load chapter ${chapterId}:`, error);
      return null;
    } finally {
      setLoadingChapters(prev => {
        const newSet = new Set(prev);
        newSet.delete(chapterId);
        return newSet;
      });
    }
  }, [loadedChapters, loadingChapters]);

  const loadChapterVersion = useCallback(async (chapterId: string, versionNumber: number): Promise<DbChapterVersion | null> => {
    try {
      const response = await apiRequest(`/api/chapters/${chapterId}/versions/${versionNumber}`, 'GET');
      return await response.json() as DbChapterVersion;
    } catch (error) {
      console.error(`Failed to load chapter version ${chapterId}/${versionNumber}:`, error);
      return null;
    }
  }, []);

  const syncChaptersFromProject = useCallback(async (projectId: number): Promise<void> => {
    try {
      const response = await apiRequest(`/api/projects/${projectId}/chapters/sync`, 'POST');
      const data = await response.json();
      
      if (data.success && data.chapters) {
        // Update cache with synced chapters
        const newCache = new Map(loadedChapters);
        data.chapters.forEach((chapter: LazyLoadedChapter) => {
          // FIX: Ensure chapter is not null before accessing its id property.
          if (chapter && chapter.id) {
            newCache.set(chapter.id, chapter);
          }
        });
        setLoadedChapters(newCache);
      }
    } catch (error) {
      console.error('Failed to sync chapters:', error);
      throw error;
    }
  }, [loadedChapters]);

  const clearCache = useCallback(() => {
    setLoadedChapters(new Map());
    setLoadingChapters(new Set());
  }, []);

  return {
    loadedChapters,
    loadingChapters,
    loadChapter,
    loadChapterVersion,
    syncChaptersFromProject,
    clearCache,
  };
};

// Hook for automatic chapter content loading when chapter is selected
export const useAutoLoadChapterContent = (
  activeChapterId: string | null,
  lazyLoading: ChapterLazyLoadingState
) => {
  const [currentChapter, setCurrentChapter] = useState<LazyLoadedChapter | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!activeChapterId) {
      setCurrentChapter(null);
      return;
    }

    const loadContent = async () => {
      setIsLoading(true);
      try {
        const chapter = await lazyLoading.loadChapter(activeChapterId);
        setCurrentChapter(chapter);
      } catch (error) {
        console.error('Failed to auto-load chapter content:', error);
        setCurrentChapter(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [activeChapterId, lazyLoading]);

  return { currentChapter, isLoading };
};
